import React, { useEffect, useRef, useState } from "react";
import { api, API_BASE } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Send, Users, User, Hash, Wrench, Plus, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Multi-channel chat with WebSocket streaming.
 * - Left: channel list (team + DMs + job threads)
 * - Right: messages + composer
 * - Fallback: polling every 5s when WebSocket disconnected
 */
export default function MultiChannelChat({ compact = false, defaultChannelId = "team" }) {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [activeId, setActiveId] = useState(defaultChannelId);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [showNewDm, setShowNewDm] = useState(false);
  const [users, setUsers] = useState([]);
  const wsRef = useRef(null);
  const pollRef = useRef(null);
  const scrollRef = useRef(null);
  const [connected, setConnected] = useState(false);

  const loadChannels = async () => {
    try {
      const { data } = await api.get("/chat-v2/channels");
      setChannels(data);
    } catch {/* ignore */}
  };

  const loadMessages = async (channelId) => {
    try {
      const { data } = await api.get(`/chat-v2/messages/${channelId}`);
      setMessages(data);
    } catch {/* ignore */}
  };

  useEffect(() => { loadChannels(); }, []);

  // Connect WebSocket whenever activeId changes
  useEffect(() => {
    if (!activeId) return;
    loadMessages(activeId);

    if (wsRef.current) { try { wsRef.current.close(); } catch {} }
    if (pollRef.current) clearInterval(pollRef.current);

    const token = localStorage.getItem("wc_token");
    const wsBase = API_BASE.replace(/^https/, "wss").replace(/^http/, "ws");
    const ws = new WebSocket(`${wsBase}/chat-v2/ws/${activeId}?token=${encodeURIComponent(token)}`);
    wsRef.current = ws;

    ws.onopen = () => setConnected(true);
    ws.onclose = () => {
      setConnected(false);
      // Polling fallback
      pollRef.current = setInterval(() => loadMessages(activeId), 5000);
    };
    ws.onmessage = (e) => {
      try {
        const evt = JSON.parse(e.data);
        if (evt.type === "message" && evt.message?.channel_id === activeId) {
          setMessages((prev) => {
            if (prev.some((m) => m.id === evt.message.id)) return prev;
            return [...prev, evt.message];
          });
        }
      } catch {}
    };

    return () => {
      try { ws.close(); } catch {}
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [activeId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    const v = text.trim();
    if (!v) return;
    setText("");
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "message", text: v }));
    } else {
      try {
        await api.post(`/chat-v2/messages/${activeId}`, { text: v });
        loadMessages(activeId);
      } catch (err) { toast.error("Send failed"); }
    }
  };

  const openNewDm = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data.filter((u) => u.id !== user?.id));
      setShowNewDm(true);
    } catch {/* ignore */}
  };

  const startDm = async (otherUser) => {
    try {
      const { data } = await api.post("/chat-v2/dm", { user_id: otherUser.id });
      setShowNewDm(false);
      await loadChannels();
      setActiveId(data.id);
    } catch (err) { toast.error("Could not start DM"); }
  };

  const active = channels.find((c) => c.id === activeId) || { id: activeId, name: activeId === "team" ? "Team" : activeId, type: activeId === "team" ? "team" : "unknown" };

  return (
    <div data-testid="multi-channel-chat" className={`flex ${compact ? "flex-col" : "flex-col md:flex-row"} gap-3 h-full`}>
      {/* Channel list */}
      <div className={`${compact ? "hidden" : "w-full md:w-56 shrink-0"} space-y-1`}>
        <div className="flex items-center justify-between px-1 mb-1">
          <div className="text-label">Channels</div>
          <button
            data-testid="chat-new-dm"
            onClick={openNewDm}
            className="p-1 rounded-full bg-white/5 hover:bg-white/10 text-slate-400"
            title="New DM"
          >
            <Plus className="w-3 h-3" />
          </button>
        </div>
        <div className="flex md:flex-col gap-1 overflow-x-auto md:overflow-x-visible md:max-h-[400px] md:overflow-y-auto">
          {channels.map((c) => (
            <ChannelBtn key={c.id} channel={c} active={c.id === activeId} onClick={() => setActiveId(c.id)} currentUser={user} />
          ))}
        </div>
      </div>

      {/* Message area */}
      <div className="flex-1 flex flex-col min-h-0">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-label flex items-center gap-1">
              {chIcon(active.type)}
              {active.type === "dm" ? "Direct message" : active.type === "job" ? "Job thread" : "Team channel"}
            </div>
            <div className="font-heading text-lg text-white leading-tight">{active.name}</div>
          </div>
          <div className={`pill ${connected ? "pill-success" : "pill-muted"}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${connected ? "bg-emerald-400" : "bg-slate-500"}`} />
            {connected ? "live" : "polling"}
          </div>
        </div>

        <div
          ref={scrollRef}
          data-testid="chat-messages"
          className={`flex-1 overflow-y-auto pr-1 space-y-3 ${compact ? "max-h-72" : "min-h-[300px] max-h-[calc(100vh-350px)]"}`}
        >
          {messages.length === 0 && (
            <div className="text-sm text-slate-500 italic py-6 text-center">
              No messages yet. Break the ice.
            </div>
          )}
          {messages.map((m) => {
            const mine = m.user_id === user?.id;
            return (
              <div key={m.id} data-testid={`chat-message-${m.id}`} className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">
                  {m.user_name} · {m.user_role}
                </div>
                <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${
                  mine ? "bg-blue-500/20 border border-blue-400/30 text-white"
                       : "bg-white/[0.04] border border-white/10 text-slate-200"
                }`}>
                  {m.text}
                </div>
              </div>
            );
          })}
        </div>

        <form onSubmit={send} className="mt-3 flex items-center gap-2">
          <input
            data-testid="chat-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Message…"
            className="flex-1 bg-black/40 border border-white/10 rounded-full px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
          />
          <button
            type="submit"
            data-testid="chat-send"
            disabled={!text.trim()}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-white text-black disabled:opacity-40 hover:bg-slate-100"
          >
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>

      {showNewDm && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-md flex items-center justify-center p-4">
          <div className="glass-strong rounded-3xl p-6 w-full max-w-sm space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-lg text-white">Start a direct message</h3>
              <button onClick={() => setShowNewDm(false)} className="p-1.5 rounded-full bg-white/5 hover:bg-white/10 text-slate-300"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-1 max-h-72 overflow-y-auto">
              {users.length === 0 && <div className="text-sm text-slate-500 italic py-4 text-center">No other users yet.</div>}
              {users.map((u) => (
                <button
                  key={u.id}
                  data-testid={`chat-dm-user-${u.id}`}
                  onClick={() => startDm(u)}
                  className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10"
                >
                  <div className="w-8 h-8 rounded-full bg-blue-500/20 border border-blue-400/30 flex items-center justify-center text-blue-300 text-xs font-bold">
                    {(u.name || u.email)[0].toUpperCase()}
                  </div>
                  <div>
                    <div className="text-sm text-white">{u.name || u.email}</div>
                    <div className="text-xs text-slate-500">{u.role}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ChannelBtn({ channel, active, onClick, currentUser }) {
  return (
    <button
      onClick={onClick}
      data-testid={`chat-channel-${channel.id}`}
      className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition text-left w-full md:w-full whitespace-nowrap md:whitespace-normal ${
        active ? "bg-white/[0.08] text-white border border-white/10" : "text-slate-400 hover:bg-white/[0.04] hover:text-white border border-transparent"
      }`}
    >
      {chIcon(channel.type)}
      <span className="truncate flex-1">{channel.name}</span>
    </button>
  );
}

function chIcon(type) {
  if (type === "team") return <Hash className="w-3.5 h-3.5" />;
  if (type === "dm") return <User className="w-3.5 h-3.5" />;
  if (type === "job") return <Wrench className="w-3.5 h-3.5" />;
  return <Users className="w-3.5 h-3.5" />;
}
